import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { parseNotificationMeta, subscribeToNotifications } from '../services/notificationService';
import TicketResolvedModal from '../components/support/TicketResolvedModal';

const SupportTicketResolvedContext = createContext({
  openResolvedTicket: () => {},
  closeResolvedTicket: () => {},
});

export function useSupportTicketResolved() {
  return useContext(SupportTicketResolvedContext);
}

export function SupportTicketResolvedProvider({ children }) {
  const { user } = useAuth();
  const [ticketId, setTicketId] = useState(null);
  const recentAutoOpen = useRef(new Set());

  const openResolvedTicket = useCallback((id) => {
    if (!id) return;
    setTicketId(String(id));
  }, []);

  const closeResolvedTicket = useCallback(() => {
    setTicketId(null);
  }, []);

  useEffect(() => {
    if (!user?.id) return undefined;

    const channel = subscribeToNotifications(
      user.id,
      (newNotif) => {
        if (!['support_ticket_resolved', 'support_ticket_reply'].includes(newNotif?.type)) return;
        const parsed = parseNotificationMeta(newNotif.message);
        const id = parsed.ticketId || newNotif.message?.match(/\[\[ticket:([^\]]+)\]\]/)?.[1];
        if (!id) return;
        const key = `${newNotif.type}:${id}`;
        if (recentAutoOpen.current.has(key)) return;
        recentAutoOpen.current.add(key);
        setTicketId(String(id));
      },
      { channelId: `support-ticket-updates:${user.id}` },
    );

    return () => channel?.unsubscribe();
  }, [user?.id]);

  return (
    <SupportTicketResolvedContext.Provider value={{ openResolvedTicket, closeResolvedTicket }}>
      {children}
      {ticketId && (
        <TicketResolvedModal ticketId={ticketId} onClose={closeResolvedTicket} />
      )}
    </SupportTicketResolvedContext.Provider>
  );
}
